import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useHomePageApi } from "@/react/hooks/home_page/useHomePageApi";
import { usePunditUserContext } from "@circle-react/contexts";
import { useShowOnboarding } from "./hooks/useShowOnboarding";

export const HOMEPAGE_TRENDING_POSTS_REQUEST_KEY =
  "homepage-trending-posts-show";
export const HOMEPAGE_UPCOMING_EVENTS_REQUEST_KEY =
  "homepage-upcoming-events-show";

interface HomepageContextProps {
  hasSidebarContent: boolean;
  trendingPosts: any[];
  upcomingEvents: any[];
}

const HomepageContext = createContext<HomepageContextProps>({
  trendingPosts: [],
  upcomingEvents: [],
  hasSidebarContent: true,
});
HomepageContext.displayName = "HomepageContext";

interface HomepageContextProviderProps {
  children: ReactNode;
}

export const HomepageContextProvider = ({
  children,
}: HomepageContextProviderProps) => {
  const { fetchPosts: fetchTrendingPosts, fetchUpcomingEvents } =
    useHomePageApi();
  const [hasSidebarContent, setHasSidebarContent] = useState(true);
  const { showOnboarding: shouldShowOnboarding } = useShowOnboarding();
  const { currentCommunityMember } = usePunditUserContext();

  const { data: trendingPosts = [] } = useQuery(
    HOMEPAGE_TRENDING_POSTS_REQUEST_KEY,
    fetchTrendingPosts,
  );

  const { data: upcomingEvents = [] } = useQuery(
    HOMEPAGE_UPCOMING_EVENTS_REQUEST_KEY,
    fetchUpcomingEvents,
  );

  useEffect(() => {
    if (!currentCommunityMember) {
      setHasSidebarContent(false);
      return;
    }

    const hasTrendingPosts = trendingPosts.length > 0;
    const hasUpcomingEvents = upcomingEvents.length > 0;

    setHasSidebarContent(
      hasUpcomingEvents || hasTrendingPosts || shouldShowOnboarding,
    );
  }, [
    trendingPosts,
    upcomingEvents,
    shouldShowOnboarding,
    currentCommunityMember,
  ]);

  const value = {
    trendingPosts,
    upcomingEvents,
    hasSidebarContent,
  };

  return (
    <HomepageContext.Provider value={value}>
      {children}
    </HomepageContext.Provider>
  );
};

export const useHomepageContext = () => useContext(HomepageContext);
